/**
 * Route `/audit/:id/advanced` (customer). Flag `audit.advanced_report` (fail-closed: loading/unknown = not shown),
 * BFF client, `?finding=<id>` deep link to the drawer. Also exports the link the audit detail page shows.
 */
import { useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useApp } from '../../../../../lib/app-context';
import { useFlag } from '../../../../../lib/flags';
import { ADVANCED_REPORT_FLAG, bffAdvancedReportApi, type AdvancedReportApi } from './api';
import { AdvancedReportView } from './AdvancedReportView';
import { ReportStore } from './store';

export function AdvancedReportPage({ api }: { api?: AdvancedReportApi }) {
  const { id = '' } = useParams();
  const { me, locale, localePrefs } = useApp();
  const flag = useFlag(ADVANCED_REPORT_FLAG);
  const [params, setParams] = useSearchParams();
  const client = useMemo(() => api ?? bffAdvancedReportApi(), [api]);
  const store = useMemo(() => new ReportStore(client, id, { contentLocale: locale === 'ar' ? 'ar' : 'en' }), [client, id, locale]);

  useEffect(() => {
    if (flag !== 'on' || !id) return;
    void store.load().then(() => { const f = params.get('finding'); if (f) store.select(f); });
    // load once per store; the finding param is read at load time only (later changes come from the view)
  }, [flag, store]);

  const onSelectionChange = (fid: string | null) => {
    setParams((cur) => { const n = new URLSearchParams(cur); if (fid) n.set('finding', fid); else n.delete('finding'); return n; }, { replace: true });
  };

  return <AdvancedReportView store={store} roles={me?.roles} flag={flag} timeZone={localePrefs.timezone} onSelectionChange={onSelectionChange} />;
}

/**
 * "Open the advanced report" link for the audit detail page. Renders nothing unless the flag is on, so the
 * detail page needs no flag logic of its own.
 */
export function AdvancedReportLink({ jobId }: { jobId: string }) {
  const { t } = useApp();
  const flag = useFlag(ADVANCED_REPORT_FLAG);
  if (flag !== 'on') return null;
  return <Link className="qm-text-link" to={`/audit/${encodeURIComponent(jobId)}/advanced`}>{t('audit-remediation.report.open')}</Link>;
}
